import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, MessageCircle, Send, CheckCircle2, User } from 'lucide-react';
import { BrandMark } from '../components/BrandMark';
import { Footer } from '../components/Footer';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const WA_NUMBER = process.env.REACT_APP_SUPPORT_WA_NUMBER || '';
const WA_PREFILL = encodeURIComponent(
  "Hi Luggagefreejourney team, I need help with my booking."
);

const initialForm = { name: '', email: '', mobile: '', baggage_tag: '', subject: '', message: '' };

const Support = () => {
  const [form, setForm] = useState(initialForm);
  const [status, setStatus] = useState('idle');
  const [error, setError] = useState('');
  const [ticketId, setTicketId] = useState('');

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!form.name.trim() || !form.email.trim() || form.message.trim().length < 10) {
      setError('Please share your name, email, and a short message (10+ characters).');
      return;
    }
    setStatus('submitting');
    try {
      const res = await axios.post(`${API}/support/contact`, { ...form, channel: 'web_form' });
      setTicketId(res.data?.ticket_id || '');
      setStatus('done');
      setForm(initialForm);
    } catch (err) {
      setStatus('error');
      setError(err?.response?.data?.detail || 'Something went wrong. Please try again.');
    }
  };

  return (
    <div data-testid="support-page" className="theme-sunset" style={{ fontFamily: 'Inter, system-ui, sans-serif' }}>
      <header className="sticky top-0 z-30 backdrop-blur-md bg-[#E8DCC9]/85 border-b border-[#1A1A1A]/10">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center">
            <BrandMark size={36} textClass="text-base sm:text-lg text-[#1A1A1A]" />
          </Link>
          <nav className="hidden sm:flex items-center gap-6 text-xs uppercase tracking-[0.25em] text-[#1A1A1A]/70">
            <Link to="/" className="hover:text-[#FF5A1F] transition-colors">Home</Link>
            <Link to="/pricing" className="hover:text-[#FF5A1F] transition-colors">Pricing</Link>
            <Link to="/faq" className="hover:text-[#FF5A1F] transition-colors">FAQ</Link>
            <Link to="/?signin=1" className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-full bg-[#1A1A1A] text-white hover:bg-[#FF5A1F] transition-colors text-xs font-bold tracking-wide no-underline">
              <User size={13} /> Sign In
            </Link>
          </nav>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 sm:px-8 py-10 sm:py-14">
        <Link to="/" className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-[#525252] hover:text-[#FF5A1F] mb-6 transition-colors" data-testid="support-back-link">
          <ArrowLeft size={14} /> Back to home
        </Link>

        <div className="text-[10px] uppercase tracking-[0.3em] text-[#FF5A1F] font-bold mb-3">Support</div>
        <h1 className="text-4xl sm:text-5xl font-black uppercase mb-4 text-[#1A1A1A]" style={{ fontFamily: 'Cabinet Grotesk', lineHeight: '1' }}>
          How can we help?
        </h1>
        <p className="text-base leading-relaxed text-[#525252] max-w-xl mb-10">
          Message us on WhatsApp for the fastest reply, or fill in the form below and we&apos;ll respond to your email within 4 working hours (Mon–Sat, 9 AM–7 PM IST).
        </p>

        <div className="grid grid-cols-1 md:grid-cols-5 gap-8">
          <aside className="md:col-span-2 border-2 border-black bg-white p-6 h-fit">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 rounded-full bg-[#25D366] flex items-center justify-center flex-shrink-0">
                <MessageCircle size={22} className="text-white" />
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-[0.3em] text-[#525252] font-bold">Fastest</div>
                <div className="text-xl font-black">Chat on WhatsApp</div>
              </div>
            </div>
            <p className="text-sm text-[#525252] mb-6 leading-relaxed">
              Reach a human on the same WhatsApp account that sends your OTPs and status updates.
            </p>
            {WA_NUMBER ? (
              <a
                href={`https://wa.me/${WA_NUMBER}?text=${WA_PREFILL}`}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="support-whatsapp-cta"
                className="inline-flex items-center gap-2 bg-[#25D366] text-white font-bold text-sm uppercase tracking-wider px-6 py-3 rounded-full hover:bg-[#1FB855] transition-colors no-underline"
              >
                <MessageCircle size={16} /> Open WhatsApp
              </a>
            ) : (
              <p className="text-xs text-[#B04832] font-mono">WhatsApp support number not configured.</p>
            )}
          </aside>

          <section className="md:col-span-3 border-2 border-black bg-[#FFFCF7] p-6">
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#525252] font-bold mb-2">Or</div>
            <h2 className="text-2xl font-black mb-4">Send us a message</h2>

            {status === 'done' ? (
              <div data-testid="support-form-success" className="border-2 border-[#22C55E] bg-[#F0FDF4] p-5">
                <div className="flex items-start gap-3">
                  <CheckCircle2 size={22} className="text-[#22C55E] flex-shrink-0 mt-0.5" />
                  <div>
                    <div className="font-black text-base">Message received.</div>
                    <p className="text-sm text-[#525252] mt-1 leading-relaxed">
                      We&apos;ve sent a confirmation to your email. Our team will reply within 4 working hours.
                    </p>
                    {ticketId && (
                      <div className="mt-3 text-xs font-mono text-[#737373]">
                        Ticket ID: <span className="font-bold">{ticketId}</span>
                      </div>
                    )}
                    <button
                      type="button"
                      onClick={() => { setStatus('idle'); setTicketId(''); }}
                      className="mt-4 text-xs uppercase tracking-wider font-bold text-[#FF4500] hover:underline"
                      data-testid="support-form-send-another"
                    >
                      Send another message →
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4" data-testid="support-form">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-[10px] uppercase tracking-[0.2em] font-bold mb-1.5">Name *</label>
                    <input type="text" name="name" value={form.name} onChange={handleChange} required
                      className="w-full border-2 border-black bg-white px-3 py-2.5 text-sm font-mono focus:outline-none focus:border-[#FF4500]"
                      data-testid="support-name-input" />
                  </div>
                  <div>
                    <label className="block text-[10px] uppercase tracking-[0.2em] font-bold mb-1.5">Email *</label>
                    <input type="email" name="email" value={form.email} onChange={handleChange} required
                      className="w-full border-2 border-black bg-white px-3 py-2.5 text-sm font-mono focus:outline-none focus:border-[#FF4500]"
                      data-testid="support-email-input" />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-[10px] uppercase tracking-[0.2em] font-bold mb-1.5">Mobile <span className="text-[#737373]">(optional)</span></label>
                    <input type="tel" name="mobile" value={form.mobile} onChange={handleChange}
                      inputMode="numeric" maxLength={10} placeholder="10-digit"
                      className="w-full border-2 border-black bg-white px-3 py-2.5 text-sm font-mono focus:outline-none focus:border-[#FF4500]"
                      data-testid="support-mobile-input" />
                  </div>
                  <div>
                    <label className="block text-[10px] uppercase tracking-[0.2em] font-bold mb-1.5">Baggage Tag <span className="text-[#737373]">(optional)</span></label>
                    <input type="text" name="baggage_tag" value={form.baggage_tag} onChange={handleChange}
                      placeholder="LFJXXXXXX"
                      className="w-full border-2 border-black bg-white px-3 py-2.5 text-sm font-mono uppercase focus:outline-none focus:border-[#FF4500]"
                      data-testid="support-baggage-tag-input" />
                  </div>
                </div>

                <div>
                  <label className="block text-[10px] uppercase tracking-[0.2em] font-bold mb-1.5">Subject <span className="text-[#737373]">(optional)</span></label>
                  <input type="text" name="subject" value={form.subject} onChange={handleChange}
                    placeholder="Refund / booking edit / other"
                    className="w-full border-2 border-black bg-white px-3 py-2.5 text-sm font-mono focus:outline-none focus:border-[#FF4500]"
                    data-testid="support-subject-input" />
                </div>

                <div>
                  <label className="block text-[10px] uppercase tracking-[0.2em] font-bold mb-1.5">Message *</label>
                  <textarea name="message" value={form.message} onChange={handleChange} required rows={5}
                    placeholder="Tell us what's going on…"
                    className="w-full border-2 border-black bg-white px-3 py-2.5 text-sm font-mono focus:outline-none focus:border-[#FF4500] resize-none"
                    data-testid="support-message-input" />
                </div>

                {error && (
                  <div className="text-xs font-mono text-[#B04832] border-2 border-[#B04832] bg-[#FEF2F2] px-3 py-2" data-testid="support-form-error">
                    {error}
                  </div>
                )}

                <button type="submit" disabled={status === 'submitting'}
                  data-testid="support-submit-button"
                  className="inline-flex items-center gap-2 bg-black text-white font-bold text-sm uppercase tracking-wider px-6 py-3 rounded-full hover:bg-[#FF4500] transition-colors disabled:opacity-60 disabled:cursor-not-allowed">
                  <Send size={14} />
                  {status === 'submitting' ? 'Sending…' : 'Send message'}
                </button>
              </form>
            )}
          </section>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default Support;
